import type { ReactNode } from 'react';
import { ArrowUpDown } from 'lucide-react';

import { AbasDaLista } from '@/components/ui/abas-da-lista';
import { BarraDaLista } from '@/components/ui/barra-da-lista';
import { FiltroDaLista, type OpcaoDoFiltro } from '@/components/ui/filtro-da-lista';
import { BuscaInteligente } from '@/components/ui/smart-search';
import type { SearchStrictness } from '@/lib/search/smartSearch';
import { diaEMes, somarDias } from '@/pages/dashboard/reguaDias';

/** As abas do acervo: a situação do processo, e os favoritos como recorte à parte. */
export type AbaDoAcervo = 'todos' | 'favoritos' | 'ATIVO' | 'SUSPENSO' | 'ENCERRADO';

const ABAS: { value: AbaDoAcervo; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'favoritos', label: 'Favoritos' },
  { value: 'ATIVO', label: 'Ativos' },
  { value: 'SUSPENSO', label: 'Suspensos' },
  { value: 'ENCERRADO', label: 'Encerrados' },
];

/** A instância deduzida; "nenhuma" junta os processos sem sinal. */
export const INSTANCIAS: OpcaoDoFiltro[] = [
  { value: 'primeiro_grau', label: '1º grau' },
  { value: 'segundo_grau', label: '2º grau' },
  { value: 'superior', label: 'Tribunal superior' },
  { value: 'nenhuma', label: 'Não informada' },
];

/** Intervalo de vencimento como valor do filtro: "AAAA-MM-DD_AAAA-MM-DD". */
export interface Vencimento {
  de: string;
  ate: string;
}

const paraValor = (v: Vencimento) => `${v.de}_${v.ate}`;

/** "Em 20 de outubro" ou "De 12 a 18 de outubro" — o intervalo que veio do painel. */
export function descreverVencimento({ de, ate }: Vencimento): string {
  return de === ate ? `Vence em ${diaEMes(de)}` : `Vence de ${diaEMes(de)} a ${diaEMes(ate)}`;
}

/** As opções do "Vence em…", contadas de hoje; um intervalo de fora (do painel) entra como opção. */
export function opcoesDeVencimento(hoje: string, atual: Vencimento | null): OpcaoDoFiltro[] {
  const opcoes = [
    { value: paraValor({ de: hoje, ate: hoje }), label: 'Vence hoje' },
    { value: paraValor({ de: somarDias(hoje, 1), ate: somarDias(hoje, 1) }), label: 'Vence amanhã' },
    { value: paraValor({ de: hoje, ate: somarDias(hoje, 6) }), label: 'Vence nos próximos 7 dias' },
    { value: paraValor({ de: somarDias(hoje, -7), ate: somarDias(hoje, -1) }), label: 'Venceu nos últimos 7 dias' },
  ];
  if (atual && !opcoes.some((o) => o.value === paraValor(atual))) {
    opcoes.push({ value: paraValor(atual), label: descreverVencimento(atual) });
  }
  return opcoes;
}

interface ProcessoFilterBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  strictness: SearchStrictness;
  onStrictnessChange: (value: SearchStrictness) => void;
  /** Contagem "N de M" dentro do campo: quantos a busca deixou dos que os filtros mostram. */
  resultCount?: number;
  totalCount?: number;
  aba: AbaDoAcervo;
  onAbaChange: (value: AbaDoAcervo) => void;
  /** Quantos a aba ativa mostra (antes da busca). */
  contagemDaAba?: number;
  /** Quem cuida (§19): só para o gestor (ausente: sem o filtro). */
  quemCuida?: { value: string; onChange: (value: string) => void; opcoes: OpcaoDoFiltro[]; padrao: string };
  vinculo: string;
  onVinculoChange: (value: string) => void;
  stats: { total: number; patrocinio: number; acompanhamento: number };
  tribunal: string;
  onTribunalChange: (value: string) => void;
  /** Tribunais presentes no acervo — não é lista fixa. */
  tribunais: string[];
  instancia: string;
  onInstanciaChange: (value: string) => void;
  hoje: string;
  vencimento: Vencimento | null;
  onVencimentoChange: (value: Vencimento | null) => void;
  /** Tags visíveis e o filtro por elas (PLANO_TAGS D7). */
  filtroTags: string[];
  onFiltroTagsChange: (ids: string[]) => void;
  /** Ordem da lista: o prazo ou audiência mais próximo (o padrão) ou a do acervo (mais recentes). */
  ordenarPorVencimento: boolean;
  onOrdenarPorVencimentoChange: (ligada: boolean) => void;
  /** O que age sobre os processos marcados (aplicar tags em lote), depois das tags. */
  selecao?: ReactNode;
  autoFocusSearch?: boolean;
}

/**
 * O topo do acervo (docs/PLANO_BARRA_DE_LISTA.md, Bloco 2), no desenho do Rubens:
 * abas da situação à esquerda e a busca no resto da linha; embaixo, os filtros.
 */
export function ProcessoFilterBar({
  searchQuery,
  onSearchChange,
  strictness,
  onStrictnessChange,
  resultCount,
  totalCount,
  aba,
  onAbaChange,
  contagemDaAba,
  quemCuida,
  vinculo,
  onVinculoChange,
  stats,
  tribunal,
  onTribunalChange,
  tribunais,
  instancia,
  onInstanciaChange,
  hoje,
  vencimento,
  onVencimentoChange,
  filtroTags,
  onFiltroTagsChange,
  ordenarPorVencimento,
  onOrdenarPorVencimentoChange,
  selecao,
  autoFocusSearch,
}: ProcessoFilterBarProps) {
  return (
    <BarraDaLista
      abas={
        <AbasDaLista
          nome="Situação"
          value={aba}
          onValueChange={onAbaChange}
          abas={ABAS.map((a) => ({ ...a, contagem: a.value === aba ? contagemDaAba : undefined }))}
        />
      }
      busca={
        <BuscaInteligente
          value={searchQuery}
          onChange={onSearchChange}
          strictness={strictness}
          onStrictnessChange={onStrictnessChange}
          resultCount={resultCount}
          totalCount={totalCount}
          placeholder="Buscar por número, parte, classe ou assunto…"
          autoFocus={autoFocusSearch}
        />
      }
      filtros={
        <>
          {/* A origem vem primeiro: os demais filtros refinam dentro dela (R13). */}
          {quemCuida && (
            <FiltroDaLista
              eixo="Quem cuida"
              value={quemCuida.value}
              onValueChange={(v) => quemCuida.onChange(v || 'todos')}
              vazio="todos"
              rotuloVazio="Qualquer um"
              opcoes={quemCuida.opcoes}
              buscavel={quemCuida.opcoes.length > 8}
            />
          )}
          <FiltroDaLista
            eixo="Vínculo"
            contagem={stats.total}
            value={vinculo}
            onValueChange={onVinculoChange}
            vazio="todos"
            rotuloVazio={`Todos os vínculos (${stats.total})`}
            opcoes={[
              { value: 'patrocinio', label: `Patrocínio (${stats.patrocinio})` },
              { value: 'acompanhamento', label: `Acompanhamento (${stats.acompanhamento})` },
            ]}
          />
          {/* Só com mais de um tribunal: com um só, o filtro nunca mudaria nada. */}
          {tribunais.length > 1 && (
            <FiltroDaLista
              eixo="Tribunal"
              value={tribunal}
              onValueChange={onTribunalChange}
              vazio="todos"
              rotuloVazio="Todos os tribunais"
              opcoes={tribunais.map((t) => ({ value: t, label: t }))}
              buscavel={tribunais.length > 8}
            />
          )}
          <FiltroDaLista
            eixo="Instância"
            value={instancia}
            onValueChange={onInstanciaChange}
            vazio="todas"
            rotuloVazio="Todas as instâncias"
            opcoes={INSTANCIAS}
          />
          <FiltroDaLista
            eixo="Vence em…"
            value={vencimento ? paraValor(vencimento) : ''}
            onValueChange={(v) => {
              const [de, ate] = v.split('_');
              onVencimentoChange(de && ate ? { de, ate } : null);
            }}
            vazio=""
            rotuloVazio="Qualquer vencimento"
            opcoes={opcoesDeVencimento(hoje, vencimento)}
          />
        </>
      }
      filtrosAtivos={
        // "Quem cuida" abre em "Você" para o gestor: só conta quando ele mudou.
        [quemCuida && quemCuida.value !== quemCuida.padrao, vinculo !== 'todos', tribunal !== 'todos', instancia !== 'todas', !!vencimento]
          .filter(Boolean).length
      }
      filtroDeTags={{ value: filtroTags, onChange: onFiltroTagsChange }}
      ordem={
        <FiltroDaLista
          eixo="Prazo ou audiência mais próximo"
          nome="Ordem"
          icone={<ArrowUpDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />}
          value={ordenarPorVencimento ? 'vencimento' : 'recentes'}
          onValueChange={(v) => onOrdenarPorVencimentoChange(v !== 'recentes')}
          vazio="vencimento"
          rotuloVazio="Prazo ou audiência mais próximo"
          opcoes={[{ value: 'recentes', label: 'Mais recentes' }]}
        />
      }
      selecao={selecao}
    />
  );
}
